import { Box, Typography, LinearProgress, Divider, FormControlLabel, Switch, Link, IconButton} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useState } from "react";
import { buildGroupSummary } from "../utils/scanGroups";

export default function ScanProgress({ accounts, scanSummary, onOpenNotScanned, onRefresh }) {
  // Scanning is a long session on one open page, so the totals drift out of date
  // unless there is a way to pull them again without reloading.
  const [refreshing, setRefreshing] = useState(false);
  const doRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try { await onRefresh(); } finally { setRefreshing(false); }
  };
  const [hideZeroCarry, setHideZeroCarry] = useState(false);
  const groupSummary = buildGroupSummary(scanSummary);

  if (!scanSummary || scanSummary.length === 0)
    return (
      <Typography sx={{ color: "gray" }}>
        No scan data available.
      </Typography>
    );

  const totalOrders = scanSummary.reduce(
    (sum, a) => sum + a.total_orders,
    0
  );
  const totalScanned = scanSummary.reduce(
    (sum, a) => sum + a.scanned_entries,
    0
  );
  const totalRemaining = totalOrders - totalScanned;
  const totalPercent =
    totalOrders > 0 ? (totalScanned / totalOrders) * 100 : 0;

  return (
    <Box
      sx={{
        width: "100%",
        padding: 2,
        backgroundColor: "#f8f8f8",
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold" }}
        >
          📊 Scan Progress
        </Typography>

        {onRefresh && (
          <IconButton onClick={doRefresh} disabled={refreshing}
                      title="Refresh scan progress" size="small"
                      sx={{ "& svg": refreshing
                              ? { animation: "spin 0.8s linear infinite" } : {},
                            "@keyframes spin": { from: { transform: "rotate(0deg)" },
                                                 to: { transform: "rotate(360deg)" } } }}>
            <RefreshIcon />
          </IconButton>
        )}

        {/* Zero Carry Filter Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={hideZeroCarry}
              onChange={(e) => setHideZeroCarry(e.target.checked)}
              size="small"
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Hide 0 Orders
            </Typography>
          }
        />
      </Box>

      {accounts.map((acc, i) => {
        const s = scanSummary.find(
          (x) => x.account_name === acc.name
        );
        if (!s || s.total_orders === 0) return null;

        // Zero carry filter: hide accounts where scanned == total (0 remaining)
        if (hideZeroCarry && s.scanned_entries >= s.total_orders) return null;

        const percent =
          (s.scanned_entries / s.total_orders) * 100;

        return (
          <Box key={i} sx={{ mb: 2 }}>
            <Typography
              sx={{ fontWeight: "bold" }}
            >
              {acc.name}: {s.scanned_entries}/{s.total_orders} (
              {percent.toFixed(2)}%)
            </Typography>

            <LinearProgress
              variant="determinate"
              value={percent}
              sx={{ height: 8, borderRadius: 5 }}
            />
          </Box>
        );
      })}

      <Divider sx={{ my: 1 }} />

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {/* Overall totals */}
        <Box>
          <Typography sx={{ fontWeight: "bold" }}>
            Total (All Accounts): {totalPercent.toFixed(2)}%
          </Typography>
          <Typography sx={{ fontWeight: "bold" }}>
            Total Orders: {totalOrders}
          </Typography>
          <Typography sx={{ fontWeight: "bold" }}>
            Total Scanned: {totalScanned}
          </Typography>
          <Typography sx={{ fontWeight: "bold" }}>
            Total Remaining: {totalRemaining}
          </Typography>
        </Box>

        {/* Group-wise breakdown (right side). Remaining is clickable. */}
        {groupSummary.length > 0 && (
          <Box
            sx={{
              minWidth: 220,
              bgcolor: "#fff",
              border: "1px solid #e0e0e0",
              borderRadius: 2,
              p: 1.5,
            }}
          >
            {groupSummary.map((g) => (
              <Box
                key={g.group}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "baseline",
                  gap: 1,
                  py: 0.25,
                }}
              >
                <Typography sx={{ fontWeight: "bold" }}>{g.group}</Typography>
                <Typography variant="body2" component="span">
                  {g.scanned}/{g.total}/
                  {onOpenNotScanned ? (
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={() => onOpenNotScanned(g.group)}
                      sx={{
                        fontWeight: "bold",
                        color: g.remaining > 0 ? "error.main" : "success.main",
                        verticalAlign: "baseline",
                      }}
                      title={`Show ${g.remaining} not-scanned in ${g.group}`}
                    >
                      -{g.remaining}
                    </Link>
                  ) : (
                    <Typography
                      component="span"
                      sx={{
                        fontWeight: "bold",
                        color: g.remaining > 0 ? "error.main" : "success.main",
                      }}
                    >
                      -{g.remaining}
                    </Typography>
                  )}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
